import { ApiError } from "./api.js";
import type { Locale } from "./i18n.js";

type TranslationShape<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : TranslationShape<T[K]>;
};

const english = {
  common: {
    requestFailed: "Request failed",
    copy: "Copy",
    close: "Close",
    never: "Never",
    name: "Name",
    status: "Status",
    email: "Email",
    role: "Role",
    type: "Type",
    value: "Value",
    version: "Version",
    created: "Created",
    save: "Save",
    cancel: "Cancel",
    refresh: "Refresh",
    siteKey: "Site Key",
    secretKey: "Secret Key",
  },
  overview: {
    description: "Live platform posture and the latest explainable risk decisions.",
    last24Hours: "Last 24 hours",
    keyMetrics: "Key metrics",
    activeSites: "Active sites",
    organizations: "Organizations",
    riskTrend: "Risk score trend",
    riskTrendDescription: "Recent server-side decisions, oldest to newest",
    dependencyProbes: "Current dependency probes",
    worker: "Worker",
    probeLatency: "Probe latency",
    decisionsDescription: "Decisions are computed by the active rule version",
    level: "Level",
  },
  organizations: {
    description: "Tenant boundaries, ownership and membership.",
    slug: "Slug",
    sites: "Sites",
    members: "Members",
  },
  organizationDetail: {
    notFound: "Organization not found",
    updated: "Organization updated.",
    updateFailed: "Update failed",
    invitationQueued: "Invitation queued for delivery.",
    invitationFailed: "Invitation failed",
    roleUpdated: "Member role updated.",
    roleUpdateFailed: "Role update failed",
    memberRemoved: "Member removed.",
    memberRemovalFailed: "Member removal failed",
    invitationRevoked: "Invitation revoked.",
    invitationRevokeFailed: "Invitation could not be revoked",
    back: "Organizations",
    plan: "Plan",
    sites: "Sites",
    members: "Members",
    verificationsThisMonth: "Verifications this month",
    organization: "Organization",
    tenantDisplayName: "Tenant display name",
    inviteMember: "Invite member",
    invitationExpiry: "Invitation links expire after seven days",
    sendInvitation: "Send invitation",
    activeAccess: "Active organization access",
    member: "Member",
    joined: "Joined",
    roleFor: "Role for",
    removeMember: "Remove member",
    invitations: "Invitations",
    invitationsDescription: "Recent invitation delivery and acceptance",
    invitedBy: "Invited by",
    expires: "Expires",
    revokeInvitation: "Revoke invitation",
    noInvitations: "No invitations",
  },
  sites: {
    description: "Site keys, registered domains and optional per-action policies.",
    organization: "Organization",
    slug: "Slug",
    environment: "Environment",
    newCredentials: "New site credentials",
    secretOnce: "The Secret Key is displayed once. Store it in the integrating server only.",
    domains: "Domains",
    actions: "Optional policies",
  },
  rules: {
    title: "Risk rules",
    description:
      "Explainable, versioned rules with mandatory human review and historical backtesting.",
    guide:
      "Rules affect the trust score: negative weight increases risk and positive weight increases trust. Approval requires a fresh backtest with at least 20 compatible samples; canary applies deterministically to 10% of sessions before full activation.",
    newDraft: "New draft",
    descriptionLabel: "Description",
    scope: "Scope",
    globalScope: "All sites (global)",
    scopeHelp:
      "Choose global only when the behavior and thresholds are valid for every customer site.",
    feature: "Feature",
    requestRate: "Request rate / 5m",
    clientRequestRate: "Anonymous client requests / 5m",
    networkRequestRate: "Network range requests / 5m",
    failedChallenges: "Failed challenges / 5m",
    clientFailedChallenges: "Anonymous client failures / 5m",
    networkFailedChallenges: "Network range failures / 5m",
    siteTrafficRatio: "Site traffic / baseline",
    webdriver: "Webdriver",
    javascriptValid: "JavaScript proof valid",
    pageVisible: "Page visible",
    pageTime: "Page time ms",
    pointerEvents: "Pointer events",
    touchEvents: "Touch events",
    keyboardEvents: "Keyboard events",
    interactionRandomness: "Interaction randomness",
    tokenReplayCount: "Token replay count",
    historicalPassRate: "Historical challenge pass rate",
    historicalChallengeCount: "Historical challenge sample count",
    networkChanged: "Network changed during session",
    operator: "Operator",
    greaterThan: "Greater than",
    greaterOrEqual: "Greater or equal",
    lessThan: "Less than",
    lessOrEqual: "Less or equal",
    equals: "Equals",
    notEquals: "Not equal",
    trueValue: "True",
    falseValue: "False",
    scoreWeight: "Score weight",
    weightHelp:
      "Negative values increase risk; positive values increase trust. Start conservatively, for example -0.05 to -0.15.",
    explainableReason: "Explainable reason",
    createDraft: "Create draft",
    weight: "Weight",
    backtest: "Backtest",
    moveTo: "Move to",
  },
  ai: {
    title: "AI workspace",
    description: "Asynchronous analysis and human-reviewed rule drafts.",
    addProvider: "Add provider",
    sections: "AI sections",
    providers: "Providers",
    analyses: "Analyses",
    suggestions: "Suggestions",
    models: "Models",
    providerSaved: "Provider saved in disabled state.",
    providerSaveFailed: "Provider could not be saved",
    providerTestPassed: "Real model inference test passed.",
    providerTestFailed: "Provider test failed",
    providerStateFailed: "Provider state could not be changed",
    providerDeleteFailed: "Provider could not be deleted",
    suggestionReviewFailed: "Suggestion review failed",
    editProvider: "Edit provider",
    newProvider: "New provider",
    baseUrl: "Base URL",
    apiKey: "API key",
    defaultModel: "Default model",
    timeout: "Timeout (ms)",
    retries: "Retries",
    concurrency: "Concurrency",
    dailyLimit: "Daily inference limit",
    monthlyLimit: "Monthly inference limit",
    limitHelp: "0 means unlimited. These limits count analysis calls, not verification traffic.",
    capacityUsage:
      "Running {running}/{concurrency} · Today {daily}/{dailyLimit} · Month {monthly}/{monthlyLimit}",
    unlimited: "unlimited",
    staleJobs: "{count} stale running job(s) pending recovery",
    defaultProvider: "Default",
    fallbackProvider: "Fallback",
    allowPrivateNetwork: "Allow private network",
    saveDisabled: "Save disabled",
    model: "Model",
    tested: "Tested",
    notTested: "Not tested",
    editProviderTitle: "Edit provider",
    deleteProvider: "Delete provider",
    test: "Test inference",
    disable: "Disable",
    enable: "Enable",
    evidence: "Evidence",
    proposedWeight: "weight",
    approveDraft: "Approve draft",
    reject: "Reject",
    featureSchema: "Feature schema",
    createdBy: "Created by",
    trained: "Trained",
    noModels: "No model versions. Production decisions remain rule-based.",
    runAnalysis: "Analyze new data",
    queueingAnalysis: "Queueing...",
    analysisQueued: "Platform telemetry analysis was queued.",
    analysisQueueFailed: "Analysis could not be queued",
    telemetryStatus: "Platform telemetry analysis status",
    telemetryTotal: "Telemetry samples",
    telemetry24h: "Last 24 hours",
    completedAnalyses: "Completed analyses",
    activeAnalysisJobs: "Active jobs",
    providerReady: "AI provider ready",
    providerRequired: "Enable a tested AI provider",
    automaticSchedule: "Automatic analysis every {minutes} minutes after {samples} new samples",
    latestTelemetry: "Latest telemetry",
    telemetryAwaitingAnalysis:
      "{count} real verification samples have been collected. Analysis is waiting for the worker or provider.",
    noTelemetry: "No verification telemetry has been collected yet.",
    platformScope: "Platform",
    samples: "samples",
    confidence: "Confidence",
    recentJobs: "Recent analysis jobs",
    recentJobsDescription: "Automatic and manually requested platform telemetry batches",
    analysisScope: "Scope",
    trigger: "Trigger",
    queuedAt: "Queued",
    apiCalls: "API calls",
    failureReason: "Failure",
    failureCodes: {
      "provider-capacity-exhausted":
        "Provider capacity unavailable (legacy undifferentiated error)",
      "provider-concurrency-exhausted": "Provider concurrency is full; the worker will retry",
      "provider-daily-limit-exhausted": "Daily AI inference limit reached",
      "provider-monthly-limit-exhausted": "Monthly AI inference limit reached",
      "no-enabled-provider": "No tested and enabled provider is available",
      "worker-interrupted": "A previous worker stopped mid-call; the slot was recovered",
      "provider-rate-limited": "Provider rate-limited the request",
      "provider-authentication-failed": "Provider rejected the API credentials",
      "provider-upstream-error": "Provider returned a server error",
      "provider-request-rejected": "Provider rejected the inference request",
      "provider-empty-response": "Provider returned no model output",
      "provider-invalid-response": "Model output did not match the required JSON schema",
      "provider-timeout": "Provider inference timed out",
      "provider-request-failed": "Provider inference request failed",
      "analysis-persistence-failed": "Analysis completed but could not be saved",
      "invalid-analysis-input": "Collected analysis data did not match the expected schema",
      "analysis-processing-failed": "Analysis processing failed",
      "analysis-job-not-claimable": "Analysis job was already claimed or changed",
      Error: "Legacy worker error; rerun after updating to get the exact reason",
    },
  },
  operations: {
    title: "Operations",
    unavailable: "Operations status unavailable",
    checked: "Checked",
    probe: "probe",
    worker: "Worker",
    heartbeat: "Heartbeat",
    noHeartbeat: "No current heartbeat",
    queues: "Queues",
    queueDescription: "BullMQ state counts",
  },
  users: {
    title: "Users",
    description: "Platform access and account status.",
    updated: "User updated.",
    updateFailed: "User update failed",
    user: "User",
    platformRole: "Platform role",
    lastLogin: "Last login",
    platformRoleFor: "Platform role for",
    noPlatformRole: "No platform role",
    securityAnalyst: "Security analyst",
    superAdmin: "Super admin",
    statusFor: "Status for",
  },
  records: {
    description: "Server-side records within the current authorization scope.",
    id: "ID",
    levelStatus: "Level / status",
  },
  accountSecurity: {
    title: "Account security",
    description: "Password, MFA, connected identity and active sessions.",
    passwordChanged: "Password changed.",
    sessionsRevoked: "other session(s) revoked.",
    passwordChangeFailed: "Password change failed",
    mfaSetupFailed: "MFA setup failed",
    mfaVerificationFailed: "MFA verification failed",
    mfaDisableFailed: "MFA could not be disabled",
    password: "Password",
    localPasswordConfigured: "Local password configured",
    noLocalPassword: "No local password",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    changePassword: "Change password",
    establishPassword: "Use the password-reset flow to establish a local credential.",
    authenticator: "Authenticator",
    recoveryCodesRemain: "recovery codes remain",
    notEnabled: "Not enabled",
    enableMfa: "Enable MFA",
    qrAlt: "Authenticator QR code",
    manualKey: "Manual key",
    authenticatorCode: "Authenticator code",
    verify: "Verify",
    recoveryCodes: "Recovery codes",
    recoveryCodesWarning: "Store these now. They will not be shown again.",
    copyAll: "Copy all",
    disableMfa: "Disable MFA",
    activeSessions: "Active sessions",
    sessions: "session(s)",
    signOutOthers: "Sign out other devices",
    session: "Session",
    lastSeen: "Last seen",
    expires: "Expires",
    current: "Current",
    verified: "Verified",
    revokeSession: "Revoke session",
    connectedIdentity: "Connected identity",
    oidcDescription: "OIDC accounts linked to this user",
    provider: "Provider",
    issuer: "Issuer",
    lastUsed: "Last used",
  },
  login: {
    title: "Security console",
    description: "Sign in with your local administrator or organization account.",
    password: "Password",
    authenticatorCode: "Authenticator code",
    recoveryCode: "Recovery code",
    useAuthenticator: "Use authenticator code",
    useRecovery: "Use a recovery code",
    failed: "Login failed",
    signIn: "Sign in",
    forgotPassword: "Forgot password?",
    createAccount: "Create account",
    continueWith: "Continue with",
  },
  auth: {
    registrationUnavailable: "Registration is unavailable",
    registrationFailed: "Registration failed",
    createAccount: "Create your account",
    checkInbox: "Check your inbox. Your organization will be created after email verification.",
    backToSignIn: "Back to sign in",
    freeStart: "Start with a free organization and production-ready verification keys.",
    freePlanLimits: "Free plan limits",
    sites: "sites",
    members: "members",
    verificationsPerMonth: "verifications/month",
    yourName: "Your name",
    workEmail: "Work email",
    organizationName: "Organization name",
    password: "Password",
    confirmPassword: "Confirm password",
    website: "Website",
    agreePrefix: "I agree to the",
    terms: "Terms",
    and: "and",
    privacy: "Privacy Policy",
    alreadyAccount: "Already have an account?",
    publicRegistrationUnavailable: "Public registration is currently unavailable.",
    verificationLinkIncomplete: "The verification link is incomplete.",
    emailVerificationFailed: "Email verification failed",
    verifyEmail: "Verify your email",
    creatingAccount: "Creating your account and organization…",
    accountReady: "Your email, account and free organization are ready.",
    signIn: "Sign in",
    registerAgain: "Start registration again",
    requestFailed: "Request failed",
    resetPassword: "Reset your password",
    resetMailSent: "If the account can receive password-reset mail, a link is on its way.",
    enterEmail: "Enter the email address for your account.",
    sendResetLink: "Send reset link",
    choosePassword: "Choose a new password",
    newPassword: "New password",
    resetLinkIncomplete: "The password-reset link is incomplete.",
    requestAnotherLink: "Request another link",
    passwordChanged: "Your password has been changed and existing sessions were revoked.",
    passwordResetFailed: "Password reset failed",
    confirmNewPassword: "Confirm new password",
    changePassword: "Change password",
    invitationLinkIncomplete: "The invitation link is incomplete.",
    invitationUnavailable: "Invitation is unavailable",
    invitationAcceptFailed: "Invitation could not be accepted",
    organizationInvitation: "Organization invitation",
    invitationAccepted: "The invitation has been accepted.",
    invited: "invited",
    displayName: "Display name",
    acceptInvitation: "Accept invitation",
  },
  siteDetail: {
    recordsLoadFailed: "Records could not be loaded",
    domainAdded: "Domain added to the allowlist.",
    domainAddFailed: "Domain could not be added",
    domainRemoved: "Domain removed.",
    domainRemovalFailed: "Domain removal failed",
    keyCreationFailed: "Key creation failed",
    newSecretKey: "New Secret Key",
    keyRotationFailed: "Key rotation failed",
    keyRevoked: "Key revoked.",
    keyRevokeFailed: "Key revocation failed",
    actionCreationFailed: "Action creation failed",
    actionUpdateFailed: "Action update failed",
    actionRemovalFailed: "Action removal failed",
    settingsUpdated: "Site settings updated.",
    updateFailed: "Site update failed",
    deletionFailed: "Site deletion failed",
    deleteConfirmPrefix: "Delete",
    deleteConfirmSuffix: "This cannot be undone from the console.",
    thisSite: "this site",
    notFound: "Site not found",
    back: "Sites",
    sections: "Site sections",
    overview: "Overview",
    integration: "Integration",
    domains: "Domains",
    keys: "Keys",
    actions: "Optional policies",
    events: "Events",
    risk: "Risk",
    audit: "Audit",
    settings: "Settings",
    shownOnce: "This value is shown once.",
    verifications: "Verifications",
    passRate: "Pass rate",
    blockRate: "Block rate",
    challengeRate: "Challenge rate",
    verificationVolume: "Verification volume",
    dailyRollups: "Daily rollups for the last 30 days",
    riskDistribution: "Risk distribution",
    errorCodes: "Error codes",
    last30Days: "Last 30 days",
    browser: "Browser",
    serverVerification: "Server verification",
    credentialBoundary: "Credential boundary",
    serverOnly: "Server only",
    tokenVerification: "Token verification",
    serverEndpoint: "Server endpoint",
    hostname: "Hostname",
    port: "Port",
    portHelp: "Optional. Leave blank for any port, or enter a specific port such as 443.",
    environment: "Environment",
    allowSubdomains: "Allow subdomains",
    addDomain: "Add domain",
    domain: "Domain",
    includesSubdomains: "Includes subdomains",
    allowed: "Allowed",
    removeDomain: "Remove domain",
    keyType: "Key type",
    createKey: "Create key",
    lastUsed: "Last used",
    transitionHours: "Transition hours",
    rotate: "Rotate",
    revokeKey: "Revoke key",
    actionName: "Action name",
    actionsOptionalTitle: "No action setup required",
    actionsOptionalDescription:
      "Once a domain and keys are configured, verification works immediately. Actions are discovered automatically; use this section only to override a specific scenario's challenge or risk thresholds, or to disable it explicitly.",
    challenge: "Challenge",
    automatic: "Automatic",
    createAction: "Create action",
    action: "Action",
    enabled: "Enabled",
    low: "Low",
    medium: "Medium",
    high: "High",
    enableAction: "Enable",
    deleteAction: "Delete action",
    verificationEvents: "Verification events",
    riskDecisions: "Risk decisions",
    auditLog: "Audit log",
    newestFirst: "Newest records first",
    exportCsv: "Export CSV",
    id: "ID",
    hostTarget: "Host / target",
    score: "Score",
    result: "Result",
    loadMore: "Load more",
    siteSettings: "Site settings",
    allowLocalhost: "Allow localhost",
    saveSettings: "Save settings",
    deleteSite: "Delete site",
    deleteDescription: "Soft-delete this site and disable verification.",
  },
  comingLater: "Coming later",
} as const;

const chinese = {
  common: {
    requestFailed: "请求失败",
    copy: "复制",
    close: "关闭",
    never: "从未",
    name: "名称",
    status: "状态",
    email: "邮箱",
    role: "角色",
    type: "类型",
    value: "值",
    version: "版本",
    created: "创建时间",
    save: "保存",
    cancel: "取消",
    refresh: "刷新",
    siteKey: "Site Key",
    secretKey: "Secret Key",
  },
  overview: {
    description: "实时查看平台安全状态和最新的可解释风险决策。",
    last24Hours: "最近 24 小时",
    keyMetrics: "关键指标",
    activeSites: "活跃网站",
    organizations: "组织",
    riskTrend: "风险评分趋势",
    riskTrendDescription: "按时间从早到晚展示近期服务端决策",
    dependencyProbes: "当前依赖服务探测结果",
    worker: "任务处理器",
    probeLatency: "探测延迟",
    decisionsDescription: "决策由当前生效的规则版本计算得出",
    level: "风险等级",
  },
  organizations: {
    description: "管理租户边界、所有权和成员关系。",
    slug: "标识",
    sites: "网站",
    members: "成员",
  },
  organizationDetail: {
    notFound: "未找到该组织",
    updated: "组织已更新。",
    updateFailed: "更新失败",
    invitationQueued: "邀请已进入发送队列。",
    invitationFailed: "邀请发送失败",
    roleUpdated: "成员角色已更新。",
    roleUpdateFailed: "角色更新失败",
    memberRemoved: "成员已移除。",
    memberRemovalFailed: "成员移除失败",
    invitationRevoked: "邀请已撤销。",
    invitationRevokeFailed: "无法撤销邀请",
    back: "组织",
    plan: "套餐",
    sites: "网站",
    members: "成员",
    verificationsThisMonth: "本月验证次数",
    organization: "组织信息",
    tenantDisplayName: "租户显示名称",
    inviteMember: "邀请成员",
    invitationExpiry: "邀请链接将在 7 天后过期",
    sendInvitation: "发送邀请",
    activeAccess: "当前拥有组织访问权限的成员",
    member: "成员",
    joined: "加入时间",
    roleFor: "设置角色：",
    removeMember: "移除成员",
    invitations: "邀请记录",
    invitationsDescription: "近期邀请的发送与接受情况",
    invitedBy: "邀请人",
    expires: "过期时间",
    revokeInvitation: "撤销邀请",
    noInvitations: "暂无邀请记录",
  },
  sites: {
    description: "管理 Site Key、授权域名和可选的场景策略。",
    organization: "所属组织",
    slug: "标识",
    environment: "环境",
    newCredentials: "新网站凭据",
    secretOnce: "Secret Key 只显示一次，请仅将它保存在接入方服务器中。",
    domains: "域名",
    actions: "可选策略",
  },
  rules: {
    title: "风险规则",
    description: "管理可解释、带版本且必须经人工审核和历史回测的风险规则。",
    guide:
      "规则直接影响可信评分：负权重提高风险，正权重增加可信度。批准前必须用当前版本完成至少 20 条兼容样本的回测；灰度阶段会稳定应用到 10% 的会话，再由管理员决定是否全量启用。",
    newDraft: "新建草稿",
    descriptionLabel: "说明",
    scope: "作用范围",
    globalScope: "全部网站（全局）",
    scopeHelp: "只有在行为和阈值适用于所有客户网站时才选择全局，否则请选择具体网站。",
    feature: "特征",
    requestRate: "5 分钟请求频率",
    clientRequestRate: "匿名客户端 5 分钟请求数",
    networkRequestRate: "IP 网段 5 分钟请求数",
    failedChallenges: "5 分钟失败挑战数",
    clientFailedChallenges: "匿名客户端 5 分钟失败数",
    networkFailedChallenges: "IP 网段 5 分钟失败数",
    siteTrafficRatio: "网站流量相对历史基线",
    webdriver: "WebDriver 标记",
    javascriptValid: "JavaScript 证明有效",
    pageVisible: "页面处于可见状态",
    pageTime: "页面停留时间（毫秒）",
    pointerEvents: "指针事件数",
    touchEvents: "触摸事件数",
    keyboardEvents: "键盘事件数",
    interactionRandomness: "交互随机度",
    tokenReplayCount: "Token 重放次数",
    historicalPassRate: "历史挑战通过率",
    historicalChallengeCount: "历史挑战样本数",
    networkChanged: "验证期间更换网络",
    operator: "运算符",
    greaterThan: "大于",
    greaterOrEqual: "大于或等于",
    lessThan: "小于",
    lessOrEqual: "小于或等于",
    equals: "等于",
    notEquals: "不等于",
    trueValue: "是",
    falseValue: "否",
    scoreWeight: "评分权重",
    weightHelp: "负数会提高风险，正数会增加可信度。建议先从 -0.05 到 -0.15 的保守权重开始。",
    explainableReason: "可解释原因",
    createDraft: "创建草稿",
    weight: "权重",
    backtest: "回测",
    moveTo: "推进到",
  },
  ai: {
    title: "AI 分析工作台",
    description: "异步分析风险数据，并由人工审核规则建议。",
    addProvider: "添加服务商",
    sections: "AI 功能分区",
    providers: "服务商",
    analyses: "分析记录",
    suggestions: "规则建议",
    models: "模型版本",
    providerSaved: "服务商已保存，当前处于禁用状态。",
    providerSaveFailed: "服务商保存失败",
    providerTestPassed: "真实模型推理测试通过。",
    providerTestFailed: "服务商连接测试失败",
    providerStateFailed: "无法修改服务商状态",
    providerDeleteFailed: "无法删除服务商",
    suggestionReviewFailed: "规则建议审核失败",
    editProvider: "编辑服务商",
    newProvider: "新增服务商",
    baseUrl: "基础 URL",
    apiKey: "API Key",
    defaultModel: "默认模型",
    timeout: "超时时间（毫秒）",
    retries: "重试次数",
    concurrency: "并发数",
    dailyLimit: "每日模型调用限额",
    monthlyLimit: "每月模型调用限额",
    limitHelp: "填写 0 表示不限制；这里限制的是 AI 分析调用次数，不是用户验证次数。",
    capacityUsage:
      "运行中 {running}/{concurrency} · 今日 {daily}/{dailyLimit} · 本月 {monthly}/{monthlyLimit}",
    unlimited: "不限",
    staleJobs: "有 {count} 个陈旧任务等待自动释放",
    defaultProvider: "默认服务商",
    fallbackProvider: "备用服务商",
    allowPrivateNetwork: "允许访问私有网络",
    saveDisabled: "保存为禁用状态",
    model: "模型",
    tested: "已测试",
    notTested: "未测试",
    editProviderTitle: "编辑服务商",
    deleteProvider: "删除服务商",
    test: "测试真实推理",
    disable: "禁用",
    enable: "启用",
    evidence: "分析依据",
    proposedWeight: "权重",
    approveDraft: "批准草稿",
    reject: "拒绝",
    featureSchema: "特征结构版本",
    createdBy: "创建人",
    trained: "训练时间",
    noModels: "暂无模型版本，生产决策仍由规则引擎执行。",
    runAnalysis: "立即分析新增数据",
    queueingAnalysis: "正在提交…",
    analysisQueued: "平台验证遥测分析已进入队列。",
    analysisQueueFailed: "无法提交分析任务",
    telemetryStatus: "平台验证遥测分析状态",
    telemetryTotal: "已采集遥测",
    telemetry24h: "最近 24 小时",
    completedAnalyses: "已完成分析",
    activeAnalysisJobs: "进行中任务",
    providerReady: "AI 服务商已就绪",
    providerRequired: "请先测试并启用 AI 服务商",
    automaticSchedule: "每 {minutes} 分钟检查一次，新增满 {samples} 条后自动分析",
    latestTelemetry: "最近采集",
    telemetryAwaitingAnalysis:
      "已采集 {count} 条真实验证数据，正在等待 Worker 或 AI 服务商完成分析。",
    noTelemetry: "尚未产生验证遥测，用户完成验证后会由平台自动采集。",
    platformScope: "全平台",
    samples: "条样本",
    confidence: "置信度",
    recentJobs: "最近分析任务",
    recentJobsDescription: "平台自动创建及管理员手动提交的遥测分析批次",
    analysisScope: "分析范围",
    trigger: "触发方式",
    queuedAt: "排队时间",
    apiCalls: "API 调用次数",
    failureReason: "失败原因",
    failureCodes: {
      "provider-capacity-exhausted": "服务商容量不可用（旧版本未区分具体原因）",
      "provider-concurrency-exhausted": "模型并发已满，Worker 会自动重试",
      "provider-daily-limit-exhausted": "已达到每日 AI 模型调用限额",
      "provider-monthly-limit-exhausted": "已达到每月 AI 模型调用限额",
      "no-enabled-provider": "没有已测试并启用的 AI 服务商",
      "worker-interrupted": "旧 Worker 调用中断，占用位置已自动释放",
      "provider-rate-limited": "AI 服务商触发频率限制",
      "provider-authentication-failed": "AI 服务商拒绝了 API 凭据",
      "provider-upstream-error": "AI 服务商返回服务器错误",
      "provider-request-rejected": "AI 服务商拒绝了推理请求",
      "provider-empty-response": "模型没有返回分析内容",
      "provider-invalid-response": "模型返回内容不符合要求的 JSON 结构",
      "provider-timeout": "模型推理超时",
      "provider-request-failed": "模型推理请求失败",
      "analysis-persistence-failed": "模型分析完成，但结果保存失败",
      "invalid-analysis-input": "采集到的分析数据结构不正确",
      "analysis-processing-failed": "分析任务处理失败",
      "analysis-job-not-claimable": "分析任务已被其他 Worker 领取或状态已变化",
      Error: "旧版本 Worker 错误；更新后重新分析即可看到准确原因",
    },
  },
  operations: {
    title: "运行状态",
    unavailable: "暂时无法获取运行状态",
    checked: "检查时间",
    probe: "探测",
    worker: "任务处理器",
    heartbeat: "最近心跳",
    noHeartbeat: "当前没有心跳记录",
    queues: "任务队列",
    queueDescription: "BullMQ 各状态任务数量",
  },
  users: {
    title: "用户",
    description: "管理平台访问权限和账号状态。",
    updated: "用户已更新。",
    updateFailed: "用户更新失败",
    user: "用户",
    platformRole: "平台角色",
    lastLogin: "最近登录",
    platformRoleFor: "设置平台角色：",
    noPlatformRole: "无平台角色",
    securityAnalyst: "安全分析员",
    superAdmin: "超级管理员",
    statusFor: "设置状态：",
  },
  records: {
    description: "当前权限范围内的服务端记录。",
    id: "ID",
    levelStatus: "等级 / 状态",
  },
  accountSecurity: {
    title: "账号安全",
    description: "管理密码、多重验证、关联身份和活跃会话。",
    passwordChanged: "密码已修改。",
    sessionsRevoked: "个其他会话已撤销。",
    passwordChangeFailed: "密码修改失败",
    mfaSetupFailed: "MFA 设置失败",
    mfaVerificationFailed: "MFA 验证失败",
    mfaDisableFailed: "无法禁用 MFA",
    password: "密码",
    localPasswordConfigured: "已设置本地密码",
    noLocalPassword: "尚未设置本地密码",
    currentPassword: "当前密码",
    newPassword: "新密码",
    confirmPassword: "确认密码",
    changePassword: "修改密码",
    establishPassword: "请通过“忘记密码”流程设置本地密码。",
    authenticator: "身份验证器",
    recoveryCodesRemain: "个恢复代码剩余",
    notEnabled: "未启用",
    enableMfa: "启用 MFA",
    qrAlt: "身份验证器二维码",
    manualKey: "手动密钥",
    authenticatorCode: "身份验证器验证码",
    verify: "验证",
    recoveryCodes: "恢复代码",
    recoveryCodesWarning: "请立即妥善保存，之后不会再次显示。",
    copyAll: "复制全部",
    disableMfa: "禁用 MFA",
    activeSessions: "活跃会话",
    sessions: "个会话",
    signOutOthers: "退出其他设备",
    session: "会话",
    lastSeen: "最近活动",
    expires: "过期时间",
    current: "当前会话",
    verified: "已验证",
    revokeSession: "撤销会话",
    connectedIdentity: "关联身份",
    oidcDescription: "与该用户关联的 OIDC 账号",
    provider: "服务商",
    issuer: "签发方",
    lastUsed: "最近使用",
  },
  login: {
    title: "安全控制台",
    description: "使用本地管理员账号或组织账号登录。",
    password: "密码",
    authenticatorCode: "身份验证器验证码",
    recoveryCode: "恢复代码",
    useAuthenticator: "使用身份验证器验证码",
    useRecovery: "使用恢复代码",
    failed: "登录失败",
    signIn: "登录",
    forgotPassword: "忘记密码？",
    createAccount: "注册账号",
    continueWith: "使用以下方式继续：",
  },
  auth: {
    registrationUnavailable: "当前无法注册",
    registrationFailed: "注册失败",
    createAccount: "创建账号",
    checkInbox: "请检查邮箱。完成邮箱验证后，系统将创建账号和组织。",
    backToSignIn: "返回登录",
    freeStart: "免费创建组织并获取可用于生产环境的验证密钥。",
    freePlanLimits: "免费套餐限额",
    sites: "个网站",
    members: "名成员",
    verificationsPerMonth: "次验证/月",
    yourName: "您的姓名",
    workEmail: "工作邮箱",
    organizationName: "组织名称",
    password: "密码",
    confirmPassword: "确认密码",
    website: "网站",
    agreePrefix: "我同意",
    terms: "服务条款",
    and: "和",
    privacy: "隐私政策",
    alreadyAccount: "已有账号？",
    publicRegistrationUnavailable: "公开注册目前不可用。",
    verificationLinkIncomplete: "邮箱验证链接不完整。",
    emailVerificationFailed: "邮箱验证失败",
    verifyEmail: "验证邮箱",
    creatingAccount: "正在创建账号和组织…",
    accountReady: "邮箱、账号和免费组织均已准备就绪。",
    signIn: "登录",
    registerAgain: "重新开始注册",
    requestFailed: "请求失败",
    resetPassword: "重置密码",
    resetMailSent: "如果该账号可以接收重置邮件，重置链接正在发送。",
    enterEmail: "请输入账号邮箱。",
    sendResetLink: "发送重置链接",
    choosePassword: "设置新密码",
    newPassword: "新密码",
    resetLinkIncomplete: "密码重置链接不完整。",
    requestAnotherLink: "重新申请链接",
    passwordChanged: "密码已修改，现有会话均已撤销。",
    passwordResetFailed: "密码重置失败",
    confirmNewPassword: "确认新密码",
    changePassword: "修改密码",
    invitationLinkIncomplete: "邀请链接不完整。",
    invitationUnavailable: "该邀请不可用",
    invitationAcceptFailed: "无法接受邀请",
    organizationInvitation: "组织邀请",
    invitationAccepted: "邀请已接受。",
    invited: "邀请了",
    displayName: "显示名称",
    acceptInvitation: "接受邀请",
  },
  siteDetail: {
    recordsLoadFailed: "记录加载失败",
    domainAdded: "域名已加入白名单。",
    domainAddFailed: "域名添加失败",
    domainRemoved: "域名已移除。",
    domainRemovalFailed: "域名移除失败",
    keyCreationFailed: "密钥创建失败",
    newSecretKey: "新的 Secret Key",
    keyRotationFailed: "密钥轮换失败",
    keyRevoked: "密钥已撤销。",
    keyRevokeFailed: "密钥撤销失败",
    actionCreationFailed: "验证操作创建失败",
    actionUpdateFailed: "验证操作更新失败",
    actionRemovalFailed: "验证操作删除失败",
    settingsUpdated: "网站设置已更新。",
    updateFailed: "网站更新失败",
    deletionFailed: "网站删除失败",
    deleteConfirmPrefix: "确定删除",
    deleteConfirmSuffix: "此操作无法在控制台中撤销。",
    thisSite: "该网站",
    notFound: "未找到该网站",
    back: "网站",
    sections: "网站功能分区",
    overview: "概览",
    integration: "接入指南",
    domains: "域名",
    keys: "密钥",
    actions: "场景策略（可选）",
    events: "验证事件",
    risk: "风险决策",
    audit: "审计日志",
    settings: "网站设置",
    shownOnce: "此值只显示一次。",
    verifications: "验证次数",
    passRate: "通过率",
    blockRate: "拦截率",
    challengeRate: "挑战率",
    verificationVolume: "验证量",
    dailyRollups: "最近 30 天的每日汇总",
    riskDistribution: "风险分布",
    errorCodes: "错误代码",
    last30Days: "最近 30 天",
    browser: "浏览器端",
    serverVerification: "服务端验证",
    credentialBoundary: "凭据使用边界",
    serverOnly: "仅服务端",
    tokenVerification: "令牌验证",
    serverEndpoint: "服务端接口",
    hostname: "域名",
    port: "端口",
    portHelp: "可选。留空表示任意端口，也可以填写 443 等指定端口。",
    environment: "环境",
    allowSubdomains: "允许子域名",
    addDomain: "添加域名",
    domain: "域名",
    includesSubdomains: "包含子域名",
    allowed: "已允许",
    removeDomain: "移除域名",
    keyType: "密钥类型",
    createKey: "创建密钥",
    lastUsed: "最近使用",
    transitionHours: "过渡时间（小时）",
    rotate: "轮换",
    revokeKey: "撤销密钥",
    actionName: "操作名称",
    actionsOptionalTitle: "无需配置验证操作",
    actionsOptionalDescription:
      "添加域名并配置 Site Key、Secret Key 后即可直接验证。系统会自动识别 login、register 等场景；这里只用于单独调整某个场景的挑战方式、风险阈值，或明确停用该场景。",
    challenge: "挑战类型",
    automatic: "自动选择",
    createAction: "创建操作",
    action: "操作",
    enabled: "已启用",
    low: "低",
    medium: "中",
    high: "高",
    enableAction: "启用操作：",
    deleteAction: "删除操作",
    verificationEvents: "验证事件",
    riskDecisions: "风险决策",
    auditLog: "审计日志",
    newestFirst: "按最新记录优先显示",
    exportCsv: "导出 CSV",
    id: "ID",
    hostTarget: "域名 / 目标",
    score: "评分",
    result: "结果",
    loadMore: "加载更多",
    siteSettings: "网站设置",
    allowLocalhost: "允许 localhost",
    saveSettings: "保存设置",
    deleteSite: "删除网站",
    deleteDescription: "软删除该网站并停用验证。",
  },
  comingLater: "后续开放",
} as const satisfies TranslationShape<typeof english>;

export const consoleMessages = { en: english, "zh-CN": chinese } as const;

export function consoleText(locale: Locale) {
  return consoleMessages[locale];
}

const codeLabels: Record<Locale, Record<string, string>> = {
  en: {
    active: "Active",
    disabled: "Disabled",
    enabled: "Enabled",
    live: "Live",
    test: "Test",
    production: "Production",
    draft: "Draft",
    testing: "Testing",
    approved: "Approved",
    canary: "Canary",
    revoked: "Revoked",
    pending: "Pending",
    accepted: "Accepted",
    expired: "Expired",
    canceled: "Canceled",
    paid: "Paid",
    exhausted: "Exhausted",
    free: "Free",
    admin: "Administrator",
    activation_code: "Activation code",
    payment: "Payment",
    paused: "Paused",
    running: "Running",
    online: "Online",
    ok: "OK",
    unknown: "Unknown",
    low: "Low",
    medium: "Medium",
    high: "High",
    pass: "Pass",
    deny: "Deny",
    challenge: "Challenge",
    customer_owner: "Owner",
    customer_developer: "Developer",
    customer_viewer: "Viewer",
    platform_security_analyst: "Security analyst",
    platform_super_admin: "Super admin",
    site: "Site Key",
    secret: "Secret Key",
    invisible: "Invisible",
    checkbox: "Checkbox",
    javascript: "JavaScript",
    slider: "Slider",
    click: "Click",
    waiting: "Waiting",
    completed: "Completed",
    queued: "Queued",
    succeeded: "Succeeded",
    failed: "Failed",
    dead_letter: "Dead letter",
    delayed: "Delayed",
    prioritized: "Prioritized",
    scheduled: "Scheduled",
    startup: "Startup",
    manual: "Manual",
    "customer-feedback": "Customer feedback",
    normal: "Normal",
    mixed: "Mixed",
    traffic_anomaly: "Traffic anomaly",
    attack_campaign: "Attack campaign",
    automation: "Automation",
    suspicious: "Suspicious",
    true: "Yes",
    false: "No",
  },
  "zh-CN": {
    active: "活跃",
    disabled: "已禁用",
    enabled: "已启用",
    live: "正式",
    test: "测试",
    production: "生产",
    draft: "草稿",
    testing: "测试中",
    approved: "已批准",
    canary: "灰度发布",
    revoked: "已撤销",
    pending: "待处理",
    accepted: "已接受",
    expired: "已过期",
    canceled: "已取消",
    paid: "已支付",
    exhausted: "已用尽",
    free: "免费套餐",
    admin: "管理员开通",
    activation_code: "激活码",
    payment: "在线支付",
    paused: "已暂停",
    running: "运行中",
    online: "在线",
    ok: "正常",
    unknown: "未知",
    low: "低",
    medium: "中",
    high: "高",
    pass: "通过",
    deny: "拦截",
    challenge: "挑战",
    customer_owner: "所有者",
    customer_developer: "开发者",
    customer_viewer: "查看者",
    platform_security_analyst: "安全分析员",
    platform_super_admin: "超级管理员",
    site: "Site Key",
    secret: "Secret Key",
    invisible: "无感验证",
    checkbox: "复选框",
    javascript: "JavaScript 挑战",
    slider: "滑块",
    click: "点击",
    waiting: "等待中",
    completed: "已完成",
    queued: "已排队",
    succeeded: "已成功",
    failed: "失败",
    dead_letter: "死信",
    delayed: "延迟中",
    prioritized: "优先队列",
    scheduled: "自动调度",
    startup: "Worker 启动",
    manual: "手动触发",
    "customer-feedback": "客户反馈",
    normal: "正常",
    mixed: "混合模式",
    traffic_anomaly: "流量异常",
    attack_campaign: "攻击活动",
    automation: "自动化行为",
    suspicious: "可疑行为",
    true: "是",
    false: "否",
  },
};

export function formatCode(locale: Locale, value: string): string {
  return codeLabels[locale][value.toLowerCase()] ?? value;
}

export function localizedError(locale: Locale, error: unknown, fallback: string): string {
  if (locale === "en") return error instanceof Error ? error.message : fallback;
  if (error instanceof ApiError) return `${fallback}（错误代码：${error.code}）`;
  if (error instanceof Error && /[\u3400-\u9fff]/u.test(error.message)) return error.message;
  return fallback;
}
